<script setup>
import { ref } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { useRouter } from 'vue-router';
import Swal from 'sweetalert2';

const props = defineProps({
  title: {
    type: String,
    default: 'O continúa con'
  },
  mode: {
    type: String,
    default: 'login', // 'login' o 'register'
    validator: (value) => ['login', 'register'].includes(value)
  }
});

const emit = defineEmits(['google-auth-success']);
const googleLoading = ref(false);
const authStore = useAuthStore();
const router = useRouter();

// Configuración de Firebase
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Inicializar Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

const handleGoogleAuth = async () => {
  if (googleLoading.value) return;
  
  try {
    googleLoading.value = true;
    
    // 1. Autenticación con Firebase
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    
    // 2. Obtener el token de ID
    const idToken = await result.user.getIdToken();
    
    // 3. Enviar los datos a tu API
    const response = await authStore.handleSocialLogin({
      id_token: idToken,
      name: result.user.displayName,
      email: result.user.email
    });

    // 4. Mostrar mensaje de éxito
    const successMessage = props.mode === 'login' 
      ? 'Has ingresado correctamente con Google'
      : 'Te has registrado correctamente con Google';

    await Swal.fire({
      icon: 'success',
      title: props.mode === 'login' ? 'Inicio de sesión exitoso' : 'Registro exitoso',
      text: successMessage,
      timer: 2000,
      showConfirmButton: false
    });

    // 5. Emitir evento
    emit('google-auth-success', response);

    // 6. Redirección basada en la respuesta
    if (response.requires_country) {
      router.push('/onboarding');
    } else {
      router.push('/dashboard');
    }

  } catch (error) {
    console.error('Error con Google:', error);
    
    Swal.fire({
      icon: 'error',
      title: 'Error de autenticación',
      text: error.response?.data?.message || `Error al ${props.mode === 'login' ? 'iniciar sesión' : 'registrarse'} con Google`,
      timer: 3000
    });
  } finally {
    googleLoading.value = false;
  }
};
</script>

<template>
  <div class="social-auth-container">
    <div class="flex items-center my-6">
      <div class="flex-grow h-px bg-white/30"></div>
      <span class="px-4 text-white/80 text-sm">{{ title }}</span>
      <div class="flex-grow h-px bg-white/30"></div>
    </div>

    <button
      @click="handleGoogleAuth"
      :disabled="googleLoading"
      class="w-full flex justify-center items-center gap-2 py-3.5 px-4 bg-white text-gray-700 font-medium rounded-lg shadow-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#99b642] transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
      type="button"
    >
      <i v-if="googleLoading" class="ri-loader-4-line animate-spin text-lg"></i>
      <i v-else class="ri-google-fill text-lg text-[#4285F4]"></i>
      <span>Continuar con Google</span>
    </button>
  </div>
</template>